// validation.js — validări de format partajate în frontend (email, CUI), aliniate 1:1 cu backend
// (backend/src/utils/userValidation.js). Pentru telefon, vezi components/ui/PhoneInput.js
// (validare reală prin libphonenumber-js).

// Email: un singur @, parte locală și domeniu fără puncte consecutive/la capete, TLD ≥ 2 litere.
// Respinge `@@`, `test@.ro`, `test@ro.`, `a..b@x.ro`, spații etc.
const EMAIL_RE =
  /^[A-Za-z0-9!#$%&'*+/=?^_`{|}~-]+(?:\.[A-Za-z0-9!#$%&'*+/=?^_`{|}~-]+)*@(?:[A-Za-z0-9](?:[A-Za-z0-9-]{0,61}[A-Za-z0-9])?\.)+[A-Za-z]{2,}$/;

export const isValidEmail = (email) => {
  if (!email) return false;
  const value = String(email).trim();
  if (!value || value.length > 254) return false;
  return EMAIL_RE.test(value);
};

// Heuristică pentru tastări greșite evidente de domeniu: ultimele două etichete identice
// (ex: „yahoo.it.it", „gmail.com.com", „x.ro.ro"). Folosită pentru feedback INSTANT în formular;
// verificarea reală că domeniul primește mail (MX) se face pe server, la salvare.
export const hasDoubledTld = (email) => {
  if (!email) return false;
  const at = String(email).lastIndexOf('@');
  if (at < 0) return false;
  const labels = String(email)
    .slice(at + 1)
    .toLowerCase()
    .split('.')
    .filter(Boolean);
  if (labels.length < 2) return false;
  return labels[labels.length - 1] === labels[labels.length - 2];
};

// Email valid pentru formular: format corect ȘI fără TLD dublat (typo evident).
export const isValidEmailLive = (email) => isValidEmail(email) && !hasDoubledTld(email);

// CUI/CIF românesc: prefix opțional „RO" (plătitor TVA), apoi 2-10 cifre + cifra de control.
// Algoritm standard MFinanțe (cheia 753217532, sumă*10 mod 11, 10 → 0).
export const isValidCui = (cui) => {
  if (!cui) return false;
  let s = String(cui).trim().toUpperCase().replace(/\s/g, '');
  if (s.startsWith('RO')) s = s.slice(2);
  if (!/^\d{2,10}$/.test(s)) return false;
  const controlKey = '753217532';
  const controlDigit = parseInt(s.charAt(s.length - 1), 10);
  let body = s.slice(0, -1);
  while (body.length < 9) body = '0' + body; // padding la stânga până la 9 cifre
  let sum = 0;
  for (let i = 0; i < 9; i++) {
    sum += parseInt(body.charAt(i), 10) * parseInt(controlKey.charAt(i), 10);
  }
  let computed = (sum * 10) % 11;
  if (computed === 10) computed = 0;
  return computed === controlDigit;
};

// Extrage data nașterii codificată în CNP. Întoarce un obiect Date (UTC) sau null dacă e imposibilă.
export const getCnpBirthDate = (cnp) => {
  const s = String(cnp || '').replace(/\D/g, '');
  if (!/^\d{13}$/.test(s)) return null;
  const sc = parseInt(s[0], 10);
  let century;
  if (sc === 1 || sc === 2) century = 1900;
  else if (sc === 3 || sc === 4) century = 1800;
  else if (sc === 5 || sc === 6) century = 2000;
  else if (sc === 7 || sc === 8) {
    const yy = parseInt(s.slice(1, 3), 10);
    century = 2000 + yy <= new Date().getUTCFullYear() ? 2000 : 1900;
  } else return null;
  const year = century + parseInt(s.slice(1, 3), 10);
  const month = parseInt(s.slice(3, 5), 10);
  const day = parseInt(s.slice(5, 7), 10);
  if (month < 1 || month > 12) return null;
  const d = new Date(Date.UTC(year, month - 1, day));
  if (d.getUTCFullYear() !== year || d.getUTCMonth() !== month - 1 || d.getUTCDate() !== day)
    return null;
  return d;
};

// Data nașterii din CNP în format ISO „YYYY-MM-DD" (pentru pre-completarea câmpului de dată).
export const cnpBirthDateISO = (cnp) => {
  const d = getCnpBirthDate(cnp);
  return d ? d.toISOString().slice(0, 10) : null;
};

// Validare CNP: 13 cifre + cifra de control (279146358279, mod 11, 10 → 1) + data nașterii validă.
export const isValidCnp = (cnp) => {
  const s = String(cnp || '').replace(/\D/g, '');
  if (!/^\d{13}$/.test(s)) return false;
  const control = '279146358279';
  let sum = 0;
  for (let i = 0; i < 12; i++) sum += parseInt(s[i], 10) * parseInt(control[i], 10);
  let c = sum % 11;
  if (c === 10) c = 1;
  if (c !== parseInt(s[12], 10)) return false;
  return getCnpBirthDate(s) !== null;
};

// Validare nume/prenume: minim 2 caractere, doar litere (cu diacritice), spații, cratimă, apostrof, punct.
const NAME_RE = /^[\p{L}][\p{L}\p{M} '.-]*$/u;
export const isValidName = (name) => {
  if (!name) return false;
  const v = String(name).trim();
  return v.length >= 2 && NAME_RE.test(v);
};

// Normalizează CUI-ul pentru trimitere/stocare: doar cifrele, cu prefix RO opțional adăugat înapoi.
// `withRoPrefix` = true întoarce „RO12345678", altfel doar cifrele.
export const formatCui = (digits, withRoPrefix) => {
  const clean = String(digits || '').replace(/\D/g, '');
  if (!clean) return '';
  return withRoPrefix ? `RO${clean}` : clean;
};
